import { PublicUrlAdapter } from '@magicdoorinc/file';
import { createMutation, toast, useLocalization } from '@magicdoorinc/ui';
import { useParams } from '@solidjs/router';
import { createStore } from 'solid-js/store';
import { createMagicDoorContext } from '~/contexts/utils';
import { leaseTransactionReceiptRepository } from '~/repositories/leaseTransactionReceiptRepository';
import { fileService } from '~/utils/file';
import { usePrintReceipt } from '~/utils/printLeaseTransactionReceipt';
import type { LeaseTransactionReceiptDto } from '~/swagger/portal/Api';

export const [LeaseTransactionReceiptProvider, useLeaseTransactionReceipt] = createMagicDoorContext('LeaseTransactionReceipt', () => {
  const params = useParams<{ leaseId: string }>();
  const { t } = useLocalization();
  const { printFile } = usePrintReceipt();

  const [receipts, setReceipts] = createStore<Record<string, LeaseTransactionReceiptDto | undefined>>({});
  const [loadingById, setLoadingById] = createStore<Record<string, boolean>>({});
  const [printingById, setPrintingById] = createStore<Record<string, boolean>>({});

  const fetchReceipt = async (transactionId: string) => {
    const hasCached = !!receipts[transactionId];
    if (!hasCached) {
      setLoadingById(transactionId, true);
    }
    try {
      const dto = await leaseTransactionReceiptRepository.getReceipt(params.leaseId, transactionId);
      setReceipts(transactionId, dto);
      return dto;
    } catch (error) {
      if (!hasCached) {
        setReceipts(transactionId, undefined);
      }
      throw error;
    } finally {
      setLoadingById(transactionId, false);
    }
  };

  const getReceipt = (transactionId: string) => {
    void fetchReceipt(transactionId);
  };

  const refetchReceipt = getReceipt;

  const send = createMutation(async (transactionId: string) => {
    const response = await leaseTransactionReceiptRepository.send(params.leaseId, transactionId);
    setReceipts(transactionId, response);
    toast.success(t('Operation successful'));
    return response;
  });

  const handleDownloadFile = createMutation(async (transactionId: string) => {
    const current = receipts[transactionId];
    if (!current?.file?.id) return;
    await fileService.downloadFile(new PublicUrlAdapter({ reference: current.file.id, name: current.file.name }));
    toast.success(t('Operation successful'));
  });

  const handlePrintFile = async (transactionId: string) => {
    setPrintingById(transactionId, true);
    try {
      const cached = receipts[transactionId];
      const fileId = cached?.file?.id ?? (await fetchReceipt(transactionId))?.file?.id;
      await printFile(fileId);
    } finally {
      setPrintingById(transactionId, false);
    }
  };

  const receiptFor = (transactionId: string | undefined) => (transactionId ? receipts[transactionId] : undefined);
  const isReceiptLoading = (transactionId: string | undefined) => {
    if (!transactionId) return false;
    if (loadingById[transactionId]) return true;
    if (receipts[transactionId]) return false;
    return loadingById[transactionId] !== false;
  };
  const isPrintLoading = (transactionId: string | undefined) => !!transactionId && !!printingById[transactionId];

  return {
    receiptFor,
    isReceiptLoading,
    isPrintLoading,
    getReceipt,
    refetchReceipt,
    send,
    handleDownloadFile,
    handlePrintFile,
  };
});
